import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Mail, Phone, Clock, Send, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import api from '../lib/api';

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/contact', form);
      setSent(true);
      toast.success('Message sent! We will get back to you shortly.');
      setForm({ name: '', email: '', subject: '', message: '' });
      setTimeout(() => setSent(false), 3000);
    } catch {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8" data-testid="contact-page">
      <div>
        <h1 className="text-3xl font-heading font-medium tracking-tight text-stone-900">Contact Us</h1>
        <p className="text-stone-500 mt-1">Have questions or need support? We are here to help.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Contact info cards */}
        <div className="space-y-4">
          <Card className="border-stone-200 shadow-sm">
            <CardContent className="pt-5 pb-4">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-[#1A4D2E]/10 flex items-center justify-center shrink-0">
                  <Mail className="w-4 h-4 text-[#1A4D2E]" />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-stone-500">Email</p>
                  <p className="text-sm text-stone-900 mt-0.5">theCallTrack@gmail.com</p>
                  <p className="text-xs text-stone-400 mt-0.5">We respond within 24 hours</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-stone-200 shadow-sm">
            <CardContent className="pt-5 pb-4">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-[#1A4D2E]/10 flex items-center justify-center shrink-0">
                  <Phone className="w-4 h-4 text-[#1A4D2E]" />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-stone-500">Phone</p>
                  <p className="text-sm text-stone-900 mt-0.5">+91 90287 76813</p>
                  <p className="text-xs text-stone-400 mt-0.5">Mon-Sat, 9 AM - 6 PM IST</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-stone-200 shadow-sm">
            <CardContent className="pt-5 pb-4">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-[#1A4D2E]/10 flex items-center justify-center shrink-0">
                  <Clock className="w-4 h-4 text-[#1A4D2E]" />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-stone-500">Support Hours</p>
                  <p className="text-sm text-stone-900 mt-0.5">Monday - Saturday</p>
                  <p className="text-xs text-stone-400 mt-0.5">9:00 AM - 6:00 PM IST</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Contact form */}
        <Card className="border-stone-200 shadow-sm lg:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-heading">Send us a Message</CardTitle>
            <CardDescription>Fill in the form and our team will get back to you</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-stone-700">Your Name</Label>
                  <Input data-testid="contact-name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Dr. John Smith" required className="mt-1" />
                </div>
                <div>
                  <Label className="text-stone-700">Email</Label>
                  <Input data-testid="contact-email" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="doctor@clinic.com" required className="mt-1" />
                </div>
              </div>
              <div>
                <Label className="text-stone-700">Subject</Label>
                <Input data-testid="contact-subject" value={form.subject} onChange={e => setForm({...form, subject: e.target.value})} placeholder="How can we help?" required className="mt-1" />
              </div>
              <div>
                <Label className="text-stone-700">Message</Label>
                <Textarea data-testid="contact-message" value={form.message} onChange={e => setForm({...form, message: e.target.value})} placeholder="Describe your question or issue..." rows={5} required className="mt-1" />
              </div>
              <Button data-testid="contact-submit" type="submit" disabled={loading} className="bg-[#1A4D2E] hover:bg-[#143B23] text-white gap-2 h-11 px-6 rounded-lg">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {sent ? 'Message Sent!' : loading ? 'Sending...' : 'Send Message'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
